/**
 * Structured Logger
 *
 * Provides consistent logging across the application with:
 * - Tagged loggers for component-specific logs
 * - Log levels (debug, info, warn, error)
 * - Production-safe error reporting (integrates with AppSignal)
 *
 * @example
 * ```ts
 * import { logger } from '@/lib/logger';
 *
 * const log = logger.tag('ProductsAPI');
 *
 * log.debug('Loading products', { page: 1 });  // Dev only
 * log.info('Products loaded', { count: 50 });  // Dev only
 * log.warn('Slow query', { duration: 2000 });  // Dev only
 * log.error('Failed to load', error, { productId: 123 });  // Console + AppSignal
 * ```
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
    [key: string]: unknown;
}

interface TaggedLogger {
    debug: (message: string, context?: LogContext) => void;
    info: (message: string, context?: LogContext) => void;
    warn: (message: string, context?: LogContext) => void;
    error: (
        message: string,
        error?: Error | unknown,
        context?: LogContext
    ) => void;
}

const isDev = import.meta.env.DEV;

/**
 * Format a log message with timestamp and tag
 */
function formatMessage(level: LogLevel, tag: string, message: string): string {
    const timestamp = new Date().toISOString();
    return `[${timestamp}] [${level.toUpperCase()}] [${tag}] ${message}`;
}

/**
 * Scrub sensitive data from context before logging
 */
function scrubContext(context: LogContext): LogContext {
    const sensitiveKeys = [
        'password',
        'token',
        'secret',
        'key',
        'authorization',
        'cookie',
    ];
    const scrubbed: LogContext = {};

    for (const [key, value] of Object.entries(context)) {
        const lowerKey = key.toLowerCase();
        if (sensitiveKeys.some((sensitive) => lowerKey.includes(sensitive))) {
            scrubbed[key] = '[REDACTED]';
        } else {
            scrubbed[key] = value;
        }
    }

    return scrubbed;
}

/**
 * Create a tagged logger instance
 */
function createTaggedLogger(tag: string): TaggedLogger {
    return {
        debug(message: string, context?: LogContext) {
            if (isDev) {
                const formatted = formatMessage('debug', tag, message);
                if (context) {
                    // Using console.info to avoid no-console error for debug
                    // eslint-disable-next-line no-console
                    console.log(formatted, scrubContext(context));
                } else {
                    // eslint-disable-next-line no-console
                    console.log(formatted);
                }
            }
        },

        info(message: string, context?: LogContext) {
            if (isDev) {
                const formatted = formatMessage('info', tag, message);
                if (context) {
                    // eslint-disable-next-line no-console
                    console.log(formatted, scrubContext(context));
                } else {
                    // eslint-disable-next-line no-console
                    console.log(formatted);
                }
            }
        },

        warn(message: string, context?: LogContext) {
            const formatted = formatMessage('warn', tag, message);
            if (context) {
                console.warn(formatted, scrubContext(context));
            } else {
                console.warn(formatted);
            }
        },

        error(message: string, error?: Error | unknown, context?: LogContext) {
            const formatted = formatMessage('error', tag, message);

            // Always log to console
            if (error && context) {
                console.error(formatted, error, scrubContext(context));
            } else if (error) {
                console.error(formatted, error);
            } else if (context) {
                console.error(formatted, scrubContext(context));
            } else {
                console.error(formatted);
            }

            // In production, errors should be reported to AppSignal
            // AppSignal is already configured via instrumentation.ts
            // Errors thrown will be caught and reported automatically
        },
    };
}

/**
 * Logger factory with tag support
 */
export const logger = {
    /**
     * Create a tagged logger for a specific component/module
     */
    tag: (tag: string): TaggedLogger => createTaggedLogger(tag),

    /**
     * Direct logging methods (use tag() for component-specific logs)
     */
    debug: (message: string, context?: LogContext) =>
        createTaggedLogger('App').debug(message, context),

    info: (message: string, context?: LogContext) =>
        createTaggedLogger('App').info(message, context),

    warn: (message: string, context?: LogContext) =>
        createTaggedLogger('App').warn(message, context),

    error: (message: string, error?: Error | unknown, context?: LogContext) =>
        createTaggedLogger('App').error(message, error, context),
};
